const MAX_SIDE = 2048;
const JPEG_QUALITY = 0.92;

/**
 * Compresses ordinary JPEG/PNG photo attachments without changing documents, animation or
 * unsupported formats. Canvas normalizes browser EXIF orientation; document attachment keeps
 * the exact original bytes and metadata.
 */
export async function compressPhotoForUpload(file: File): Promise<File> {
  if (file.type !== 'image/jpeg' && file.type !== 'image/png') return file;

  let bitmap: ImageBitmap | null = null;
  let imageElement: HTMLImageElement | null = null;
  let objectUrl: string | null = null;
  try {
    if (typeof createImageBitmap === 'function') {
      bitmap = await createImageBitmap(file);
    } else {
      imageElement = await loadImage(file);
      objectUrl = imageElement.src;
    }
    const source: CanvasImageSource = bitmap ?? imageElement!;
    const sourceWidth = bitmap?.width ?? imageElement!.naturalWidth;
    const sourceHeight = bitmap?.height ?? imageElement!.naturalHeight;
    const scale = Math.min(1, MAX_SIDE / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(source, 0, 0, width, height);

    const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, JPEG_QUALITY));
    if (!blob || blob.size === 0 || blob.size >= file.size) return file;
    return new File([blob], mime === 'image/jpeg' ? replaceExtension(file.name, 'jpg') : file.name, {
      type: mime,
      lastModified: file.lastModified,
    });
  } catch {
    // If this browser cannot decode/encode this image, preserve the original attachment.
    return file;
  } finally {
    bitmap?.close();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось декодировать изображение')); // i18n-ok
    };
    image.src = url;
  });
}

function replaceExtension(name: string, extension: string): string {
  const dot = name.lastIndexOf('.');
  return `${dot > 0 ? name.slice(0, dot) : name}.${extension}`;
}
